import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react';
import { useCallback } from 'react';

export interface UnifiedDragOptions {
  /** 是否在起始事件上调用 preventDefault */
  preventDefault?: boolean;
  /** 是否在起始事件上调用 stopPropagation */
  stopPropagation?: boolean;
  /** touchmove 监听是否使用 passive，默认 false 以允许阻止滚动 */
  passiveTouchMove?: boolean;
  /** 是否在移动过程中对 touchmove 调用 preventDefault，默认 true */
  preventDefaultOnMove?: boolean;
}

export type DragMoveHandler = (clientX: number, rawEvent: MouseEvent | TouchEvent) => void;
export type DragEndHandler = (rawEvent: MouseEvent | TouchEvent) => void;

/**
 * 统一的拖拽事件 Hook（mouse/touch）
 * 仅负责事件绑定与清理，将坐标转换为 clientX 回调给上层
 */
export const useUnifiedDrag = (options?: UnifiedDragOptions) => {
  const {
    preventDefault = true,
    stopPropagation = true,
    passiveTouchMove = false,
    preventDefaultOnMove = true,
  } = options ?? {};

  const startDrag = useCallback(
    (startEvent: ReactMouseEvent | ReactTouchEvent, onMove: DragMoveHandler, onEnd?: DragEndHandler) => {
      if (preventDefault) startEvent.preventDefault();
      if (stopPropagation) startEvent.stopPropagation();

      const handleMove = (e: MouseEvent | TouchEvent) => {
        if ('touches' in e && preventDefaultOnMove) {
          // 阻止页面在拖拽过程中滚动
          e.preventDefault();
        }
        const clientX = 'touches' in e ? (e.touches[0]?.clientX ?? 0) : (e as MouseEvent).clientX;
        onMove(clientX, e);
      };

      const handleEnd = (e: MouseEvent | TouchEvent) => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
        document.removeEventListener('touchcancel', handleEnd);
        onEnd?.(e);
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: passiveTouchMove });
      document.addEventListener('touchend', handleEnd);
      document.addEventListener('touchcancel', handleEnd);
    },
    [preventDefault, stopPropagation, passiveTouchMove, preventDefaultOnMove],
  );

  return { startDrag };
};
